/**
 * Auto Evidence Page — coverage dashboard driven by evidence_collector.
 * For each enabled control, shows automatically collected evidence from:
 *  - Manual uploads
 *  - Policy acknowledgements
 *  - Training completions
 *  - CSPM (Amidala/Prowler) findings
 *  - LiteSabre SAST/DAST findings
 * Refreshed nightly by scheduler; manual refresh button available.
 */
import React, { useEffect, useState } from 'react';
import {
  Sparkles, RefreshCcw, ShieldCheck, ShieldAlert, ShieldQuestion, ChevronRight,
  BookOpen, FileText, Cloud, Bug, Upload, Filter, ArrowLeft, Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/services/api';

const FRAMEWORKS = [
  { code: 'SOC2', name: 'SOC 2 Type II' },
  { code: 'ISO27001', name: 'ISO 27001:2022' },
  { code: 'NIST-CSF', name: 'NIST CSF 2.0' },
  { code: 'NIST-800-53', name: 'NIST 800-53' },
  { code: 'HIPAA', name: 'HIPAA' },
  { code: 'DPDPA-IN', name: 'DPDPA (India)' },
];

const SOURCE_META = {
  upload: { label: 'Upload', icon: Upload, color: 'bg-slate-100 text-slate-700 border-slate-200' },
  policy_ack: { label: 'Policy Ack', icon: FileText, color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  training: { label: 'Training', icon: BookOpen, color: 'bg-purple-100 text-purple-700 border-purple-200' },
  cspm: { label: 'CSPM', icon: Cloud, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  litesabre: { label: 'LiteSabre', icon: Bug, color: 'bg-amber-100 text-amber-700 border-amber-200' },
};

const AutoEvidencePage = () => {
  const [framework, setFramework] = useState('SOC2');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [selectedControl, setSelectedControl] = useState(null); // control detail view
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');

  useEffect(() => {
    load();
  }, [framework]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/grc/auto-evidence/${framework}`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load evidence');
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const res = await api.post('/grc/auto-evidence/refresh', { framework });
      toast.success(`Refreshed ${res.data.refreshed} controls`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const openDetail = async (controlId) => {
    setSelectedControl(controlId);
    setDetailLoading(true);
    setDetailData(null);
    try {
      const res = await api.get(`/grc/auto-evidence/${framework}/${controlId}`);
      setDetailData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load control evidence');
    } finally {
      setDetailLoading(false);
    }
  };

  const items = data?.items || [];
  const filtered = items.filter((c) => {
    if (levelFilter !== 'all' && c.coverage_level !== levelFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (c.control_id || '').toLowerCase().includes(q) ||
             (c.display_name || '').toLowerCase().includes(q) ||
             (c.control_name || '').toLowerCase().includes(q);
    }
    return true;
  });

  const summary = data?.summary || {};

  if (selectedControl) {
    return (
      <ControlDetailView
        controlId={selectedControl}
        framework={framework}
        data={detailData}
        loading={detailLoading}
        onBack={() => { setSelectedControl(null); setDetailData(null); }}
      />
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-7xl mx-auto" data-testid="auto-evidence-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading tracking-tight flex items-center gap-2">
            <Sparkles className="w-8 h-8 text-indigo-600" />
            Auto Evidence
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Evidence for enabled controls is auto-collected from your CSPM scans, policy acks, training, and LiteSabre findings.
            Refreshed nightly.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={framework} onValueChange={setFramework}>
            <SelectTrigger className="w-48" data-testid="framework-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FRAMEWORKS.map((f) => (
                <SelectItem key={f.code} value={f.code}>{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={refresh} disabled={refreshing} data-testid="refresh-evidence">
            <RefreshCcw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh now'}
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <SummaryCard label="Total controls" value={summary.total || 0} icon={ShieldQuestion} color="slate" />
        <SummaryCard label="Strong coverage" value={summary.strong || 0} icon={ShieldCheck} color="emerald" testid="stat-strong" />
        <SummaryCard label="Moderate" value={summary.moderate || 0} icon={ShieldAlert} color="amber" testid="stat-moderate" />
        <SummaryCard label="Weak / no evidence" value={summary.weak || 0} icon={ShieldAlert} color="red" testid="stat-weak" />
      </div>

      {/* Avg */}
      <Card className="mb-6" data-testid="avg-card">
        <CardContent className="p-4 flex items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-semibold">Average evidence coverage</span>
              <span className="text-sm text-muted-foreground">{summary.avg_coverage_pct || 0}%</span>
            </div>
            <Progress value={summary.avg_coverage_pct || 0} className="h-2" />
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search control ID or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="control-search"
          />
        </div>
        <Select value={levelFilter} onValueChange={setLevelFilter}>
          <SelectTrigger className="w-40" data-testid="level-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All coverage</SelectItem>
            <SelectItem value="strong">Strong</SelectItem>
            <SelectItem value="moderate">Moderate</SelectItem>
            <SelectItem value="weak">Weak</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>No controls match your filter. Try refreshing or adjusting filters.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 divide-y">
            {filtered.map((c) => (
              <div
                key={c.control_id}
                onClick={() => openDetail(c.control_id)}
                className="flex items-center gap-4 p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                data-testid={`control-row-${c.control_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm">{c.display_name || c.control_id}</p>
                    <CoverageBadge level={c.coverage_level} pct={c.coverage_pct} />
                  </div>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {Object.entries(c.counts_by_source || {}).map(([src, count]) => count > 0 && (
                      <SourceChip key={src} source={src} count={count} />
                    ))}
                    {Object.values(c.counts_by_source || {}).every((n) => !n) && (
                      <span className="text-xs text-muted-foreground">No evidence collected yet</span>
                    )}
                  </div>
                </div>
                <div className="w-32">
                  <Progress value={c.coverage_pct || 0} className="h-1.5" />
                  <p className="text-xs text-muted-foreground text-right mt-1">{c.coverage_pct || 0}%</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, icon: Icon, color, testid }) => {
  const colorClasses = {
    slate: 'border-l-slate-500',
    emerald: 'border-l-emerald-500 bg-emerald-50/30',
    amber: 'border-l-amber-500 bg-amber-50/30',
    red: 'border-l-red-500 bg-red-50/30',
  };
  const iconColors = { slate: 'text-slate-400', emerald: 'text-emerald-500', amber: 'text-amber-500', red: 'text-red-500' };
  return (
    <Card className={`border-l-4 ${colorClasses[color]}`} data-testid={testid}>
      <CardContent className="pt-5 pb-4 flex items-center justify-between">
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
        </div>
        <Icon className={`w-8 h-8 ${iconColors[color]} opacity-40`} />
      </CardContent>
    </Card>
  );
};

const CoverageBadge = ({ level, pct }) => {
  const map = {
    strong: { cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: ShieldCheck },
    moderate: { cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: ShieldAlert },
    weak: { cls: 'bg-red-100 text-red-700 border-red-200', icon: ShieldAlert },
  };
  const m = map[level] || map.weak;
  const Icon = m.icon;
  return (
    <Badge className={`${m.cls} gap-1 text-[10px] h-5`}>
      <Icon className="w-3 h-3" />
      {level} ({pct || 0}%)
    </Badge>
  );
};

const SourceChip = ({ source, count }) => {
  const m = SOURCE_META[source] || { label: source, icon: Sparkles, color: 'bg-slate-100 text-slate-700 border-slate-200' };
  const Icon = m.icon;
  return (
    <Badge variant="outline" className={`${m.color} gap-1 text-[10px] h-5`}>
      <Icon className="w-3 h-3" />
      {m.label} · {count}
    </Badge>
  );
};

// -----------------------------------------------------------
// Detail view
// -----------------------------------------------------------
const ControlDetailView = ({ controlId, framework, data, loading, onBack }) => {
  if (loading || !data) {
    return (
      <div className="p-6 md:p-8 lg:p-10 max-w-5xl mx-auto">
        <Button variant="outline" size="sm" onClick={onBack} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
        </Button>
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-slate-200 rounded w-1/2"></div>
          <div className="h-40 bg-slate-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-5xl mx-auto" data-testid="control-detail-view">
      <Button variant="outline" size="sm" onClick={onBack} className="mb-4" data-testid="detail-back">
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to controls
      </Button>
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="outline" className="text-xs">{framework}</Badge>
          {data.domain && <Badge variant="outline" className="text-xs">{data.domain}</Badge>}
          <CoverageBadge level={data.coverage_level} pct={data.coverage_pct} />
        </div>
        <h1 className="text-3xl font-bold tracking-tight">{data.display_name}</h1>
        {data.requirement_text && (
          <p className="text-sm text-muted-foreground mt-2 max-w-3xl">{data.requirement_text}</p>
        )}
      </div>

      {/* Coverage strip */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold text-sm">Evidence coverage</p>
            <p className="text-2xl font-bold">{data.coverage_pct}%</p>
          </div>
          <Progress value={data.coverage_pct} className="h-2" />
          <div className="flex gap-2 flex-wrap mt-3">
            {Object.entries(data.counts_by_source || {}).map(([src, cnt]) => (
              <SourceChip key={src} source={src} count={cnt} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Items */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="w-5 h-5 text-indigo-500" />
            Collected Evidence ({data.items?.length || 0})
          </CardTitle>
          <CardDescription>
            Auto-collected from all connected sources. Auditors can trace each item back to its source.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0 divide-y">
          {(data.items || []).length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No evidence collected yet. Try mapping policies/training to this control, or add manual uploads.
            </div>
          )}
          {(data.items || []).map((it, i) => (
            <EvidenceItem key={i} item={it} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

const EvidenceItem = ({ item }) => {
  const m = SOURCE_META[item.source] || { label: item.source, icon: Sparkles };
  const Icon = m.icon;
  const stamp = item.timestamp ? new Date(item.timestamp).toLocaleString() : '—';
  return (
    <div className="p-4 flex items-start gap-4 hover:bg-slate-50" data-testid={`evidence-item-${item.id || item.title}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${m.color?.replace('text-', 'text-').split(' ')[0] || 'bg-slate-100'}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">{item.title}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <Badge variant="outline" className="text-[10px] h-5">{m.label}</Badge>
          <Badge variant="outline" className="text-[10px] h-5 capitalize">{item.status || 'active'}</Badge>
          {item.meta?.severity && (
            <Badge className={`text-[10px] h-5 ${
              ['high', 'critical'].includes((item.meta.severity || '').toLowerCase())
                ? 'bg-red-100 text-red-700 border-red-200'
                : 'bg-slate-100 text-slate-700'
            }`}>{item.meta.severity}</Badge>
          )}
          {item.meta?.completion_pct !== undefined && (
            <span className="text-xs text-muted-foreground">
              {item.meta.acknowledged ?? item.meta.completions} / {item.meta.assigned} ({item.meta.completion_pct}%)
            </span>
          )}
          <span className="text-xs text-muted-foreground">{stamp}</span>
        </div>
      </div>
      {item.url && (
        <a href={item.url} target="_blank" rel="noreferrer" className="text-indigo-600 text-xs hover:underline">
          View
        </a>
      )}
    </div>
  );
};

export default AutoEvidencePage;
